/**
 * useIdleNudge Hook
 * 
 * In push-to-talk (PTT) mode, the user controls when to speak. This means
 * the avatar can finish talking and then... silence. The conversation feels
 * "frozen" because time effectively stops when the user isn't pressing the
 * push-to-talk button.
 * 
 * This hook monitors for extended silence after the avatar finishes speaking
 * and triggers a "nudge" — a system message that prompts the avatar to
 * naturally check if the user is still there (e.g., "Did you hear me?",
 * "Everything okay?"), staying fully in character.
 * 
 * Rules:
 * - Only active in PTT mode, after the greeting is complete
 * - Random delay between 15-30 seconds after avatar finishes speaking
 * - Maximum 2 nudges per silence period (resets when user speaks)
 * - Timer resets when user speaks, avatar speaks, or audio plays
 */

import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';

const NUDGE_MIN_DELAY_MS = 15000;  // 15 seconds minimum
const NUDGE_MAX_DELAY_MS = 30000;  // 30 seconds maximum
const MAX_NUDGES_PER_SILENCE = 2; // Stop nudging after 2 unanswered nudges

interface UseIdleNudgeOptions {
  /** Whether we're in PTT input mode */
  isPTTMode: boolean;
  /** Whether the greeting is still in progress */
  isGreetingActive: boolean;
  /** Whether the user is currently speaking (PTT pressed) */
  isUserSpeaking: boolean;
  /** Whether the AI has a response in progress */
  aiResponseInProgress: boolean;
  /** Whether avatar audio is currently playing */
  isAudioPlaying: boolean;
  /** Whether the session is paused */
  isPaused: boolean;
  /** Whether the session is in analysis mode (technician debugging) */
  isAnalysisMode: boolean;
  /** Callback to send the nudge system message */
  onNudge: () => void;
}

export function useIdleNudge({
  isPTTMode,
  isGreetingActive,
  isUserSpeaking,
  aiResponseInProgress,
  isAudioPlaying,
  isPaused,
  isAnalysisMode,
  onNudge,
}: UseIdleNudgeOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeCountRef = useRef(0);
  const onNudgeRef = useRef(onNudge);

  // Keep callback ref fresh
  useEffect(() => {
    onNudgeRef.current = onNudge;
  }, [onNudge]);

  // Reset nudge count when user speaks (they're back in the conversation)
  const prevIsUserSpeakingRef = useRef(false);
  useEffect(() => {
    if (isUserSpeaking && !prevIsUserSpeakingRef.current) {
      // User started speaking — reset nudge counter
      nudgeCountRef.current = 0;
    }
    prevIsUserSpeakingRef.current = isUserSpeaking;
  }, [isUserSpeaking]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Determine if the conversation is in an "idle" state where nudging makes sense:
    // - PTT mode active
    // - Greeting is done
    // - User is NOT speaking
    // - AI is NOT generating a response
    // - Audio is NOT playing (avatar finished talking)
    // - Session is NOT paused
    // - NOT in analysis mode (technician debugging — nudges make no sense here)
    // - We haven't exceeded the nudge limit
    const isIdle = (
      isPTTMode &&
      !isGreetingActive &&
      !isUserSpeaking &&
      !aiResponseInProgress &&
      !isAudioPlaying &&
      !isPaused &&
      !isAnalysisMode
    );

    if (isIdle && nudgeCountRef.current < MAX_NUDGES_PER_SILENCE) {
      // Start a random timer
      const delay = NUDGE_MIN_DELAY_MS + Math.random() * (NUDGE_MAX_DELAY_MS - NUDGE_MIN_DELAY_MS);
      
      logger.debug(`[⏳ IDLE NUDGE] Starting timer: ${Math.round(delay / 1000)}s (nudge #${nudgeCountRef.current + 1})`);
      
      clearTimer();
      timerRef.current = setTimeout(() => {
        logger.info(`[⏳ IDLE NUDGE] Timer fired — sending nudge #${nudgeCountRef.current + 1}`);
        nudgeCountRef.current += 1;
        onNudgeRef.current();
      }, delay);
    } else {
      // Not idle — clear any pending timer
      clearTimer();
    }

    return clearTimer;
  }, [isPTTMode, isGreetingActive, isUserSpeaking, aiResponseInProgress, isAudioPlaying, isPaused, isAnalysisMode, clearTimer]);
}
